import { useState, useEffect, useRef } from 'react';

/**
 * Hook that animates a number from 0 to target over a given duration.
 * Uses easeOutExpo for a snappy, premium feel.
 */
export function useCountUp(target, duration = 500) {
  const [value, setValue] = useState(0);
  const prevTarget = useRef(0);
  const rafId = useRef(null);

  useEffect(() => {
    const num = typeof target === 'number' ? target : parseInt(target, 10) || 0;
    const start = prevTarget.current;
    prevTarget.current = num;

    if (num === start) { setValue(num); return; }

    const startTime = performance.now();
    const diff = num - start;

    const tick = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // easeOutExpo: fast start, smooth deceleration
      const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setValue(Math.round(start + diff * eased));
      if (progress < 1) {
        rafId.current = requestAnimationFrame(tick);
      }
    };

    rafId.current = requestAnimationFrame(tick);
    return () => { if (rafId.current) cancelAnimationFrame(rafId.current); };
  }, [target, duration]);

  return value;
}

/**
 * Component wrapper — drop-in replacement for a raw number.
 * <AnimatedNumber value={557} /> renders "557" with a count-up animation.
 */
export function AnimatedNumber({ value, duration = 500, className = '' }) {
  const display = useCountUp(value, duration);
  return <span className={className}>{display.toLocaleString()}</span>;
}
